import React, { useState } from 'react';
import { authClient } from '../../authClient.js';
import { PASSWORD_POLICY_MESSAGE, PASSWORD_RULE_TEXT, meetsPasswordPolicy } from '../../passwordPolicy.js';
import { PawsMark, WarnCircle } from '../welcome/icons.js';
import './auth.css';

/** Landing page for the password-reset email link (/reset-password?token=…). */
export const ResetPassword: React.FC = () => {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!meetsPasswordPolicy(password)) {
      setError(PASSWORD_POLICY_MESSAGE);
      return;
    }
    setBusy(true);
    try {
      await authClient.resetPassword(password, token);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed. The link may have expired.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pf-auth">
      <div className="pf-auth-card">
        <div className="pf-auth-hero">
          <div className="pf-auth-mark"><PawsMark size={72} /></div>
          <div className="pf-auth-wordmark">Paws<em>Free</em></div>
          <div className="pf-auth-eyebrow">Account dashboard</div>
        </div>

        <div className="pf-auth-body">
          <h1 className="pf-auth-title">{done ? 'Password updated' : 'Choose a new password'}</h1>

          {!token && !done && (
            <p className="pf-auth-sub">
              This reset link is missing its token. Request a new one from the sign-in page.
            </p>
          )}

          {done ? (
            <>
              <p className="pf-auth-sub">Your password has been reset. You can sign in now.</p>
              <a href="/portal" className="pf-auth-cta">Go to sign in</a>
            </>
          ) : token ? (
            <form className="pf-auth-form" onSubmit={submit}>
              <label className="pf-auth-field">
                <span>New password</span>
                <input
                  className="pf-auth-input"
                  type="password"
                  placeholder="At least 12 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  aria-describedby="pf-pw-rule"
                />
                {/* Not a <span>: .pf-auth-field > span is the bold label style. */}
                <small className="pf-auth-hint" id="pf-pw-rule">{PASSWORD_RULE_TEXT}</small>
              </label>

              {error && <div className="pf-auth-msg pf-auth-err" role="alert"><WarnCircle />{error}</div>}

              <button type="submit" className="pf-auth-cta" disabled={busy}>
                {busy ? 'Saving…' : 'Reset password'}
              </button>
            </form>
          ) : (
            <a href="/portal" className="pf-auth-cta">Go to sign in</a>
          )}
        </div>
      </div>
    </div>
  );
};
